import React, { useEffect, useState } from "react";
import { X, Check, Pencil, Lock, AlertTriangle } from "lucide-react";
import PayrollDetailsSection from "./PayrollDetailsSection";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const inputCls = "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const SECTIONS = {
  "Personal":    ["first_name","last_name","nationality","gender","age","personal_email","personal_phone"],
  "Work Contact":["email","work_phone"],
  "Employment":  ["employee_code","title","department","location","band","employment_type","start_date","notice_period","probation_end"],
  "Reporting":   ["manager","manager_email"],
  // Activation status is managed via the Deactivate/Reactivate quick actions
  // above — not as an editable field here — so a profile save can never flip
  // is_active. Only role remains editable in this section.
  "Access":      ["role"],
  "Salary":      ["annual_salary","currency","pay_group"],
  "Address":     ["address","city","postal_code"],
};

const FIELD_LABELS = {
  first_name: "First Name", last_name: "Last Name", nationality: "Nationality",
  gender: "Gender", age: "Age", personal_email: "Personal Email", personal_phone: "Personal Phone",
  email: "Work Email", work_phone: "Work Phone", employee_code: "Employee ID",
  title: "Job Title", department: "Department", location: "Location", band: "Band",
  employment_type: "Employment Type", start_date: "Start Date", notice_period: "Notice Period (days)",
  probation_end: "Probation End Date", manager: "Manager Name", manager_email: "Manager Email",
  role: "Role", status: "Status", annual_salary: "Annual Salary", currency: "Currency",
  pay_group: "Pay Group", address: "Address", city: "City", postal_code: "Postcode",
};

const SELECT_FIELDS = {
  employment_type: ["Full-time","Part-time","Contract","Intern","Freelance"],
  status:          ["active","inactive","offboarded"],
  currency:        ["GBP","USD","EUR","INR","SGD"],
  pay_group:       ["Monthly","Weekly","Bi-weekly"],
  gender:          ["Male","Female","Non-binary","Prefer not to say"],
};

const MULTI_SELECT_FIELDS = {
  role: ["staff","manager","admin","executive","superadmin"],
};

function RoleMultiSelect({ value, onChange }) {
  // value is the roles array/string from draft.roles
  const selected = Array.isArray(value)
    ? value
    : value ? value.split(",").map(r => r.trim()).filter(Boolean) : [];

  const toggle = (role) => {
    const next = selected.includes(role)
      ? selected.filter(r => r !== role)
      : [...selected, role];
    // Save as comma-separated string for backend compatibility
    onChange(next.join(","));
  };

  const ROLE_STYLES = {
    staff:      "border-white/20 text-white/50",
    manager:    "border-[#00ff90]/40 text-[#00ff90]/70",
    admin:      "border-[#60a5fa]/40 text-[#60a5fa]/70",
    executive:  "border-[#e879f9]/40 text-[#e879f9]/70",
    superadmin: "border-[#fbbf24]/40 text-[#fbbf24]/70",
  };
  const ROLE_ACTIVE = {
    staff:      "bg-white/10 border-white/40 text-white",
    manager:    "bg-[#00ff90]/10 border-[#00ff90]/60 text-[#00ff90]",
    admin:      "bg-[#60a5fa]/10 border-[#60a5fa]/60 text-[#60a5fa]",
    executive:  "bg-[#e879f9]/10 border-[#e879f9]/60 text-[#e879f9]",
    superadmin: "bg-[#fbbf24]/10 border-[#fbbf24]/60 text-[#fbbf24]",
  };

  return (
    <div>
      <label className={labelCls}>Role</label>
      <div className="flex flex-wrap gap-2 mt-1">
        {MULTI_SELECT_FIELDS.role.map(role => {
          const active = selected.includes(role);
          return (
            <button
              key={role}
              type="button"
              onClick={() => toggle(role)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-all capitalize font-medium ${
                active ? ROLE_ACTIVE[role] : `bg-white/3 ${ROLE_STYLES[role]} hover:opacity-80`
              }`}
            >
              {role}
            </button>
          );
        })}
      </div>
      {selected.length === 0 && (
        <p className="text-xs text-red-400/50 mt-1">At least one role required</p>
      )}
    </div>
  );
}

function Field({ fieldKey, value, onChange, draft, setDraft }) {
  const label = FIELD_LABELS[fieldKey] || fieldKey;
  const opts  = SELECT_FIELDS[fieldKey];

  if (fieldKey === "role") {
    // Use draft.roles for the multi-select, not draft.role
    return (
      <RoleMultiSelect
        value={draft?.roles}
        onChange={v => setDraft(d => ({ ...d, roles: v }))}
      />
    );
  }

  return (
    <div>
      <label className={labelCls}>{label}</label>
      {opts ? (
        <select className={inputCls} value={value || ""} onChange={e => onChange(e.target.value)}>
          <option value="">Select…</option>
          {opts.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input className={inputCls} value={value || ""} onChange={e => onChange(e.target.value)}
          type={fieldKey.includes("date") || fieldKey.includes("end") ? "date" : fieldKey === "age" || fieldKey.includes("salary") || fieldKey.includes("period") ? "number" : "text"} />
      )}
    </div>
  );
}

export default function UserEditDrawer({ user, onClose, onSaved }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft,   setDraft]   = useState({});
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [error,   setError]   = useState(null);
  const [confirm, setConfirm] = useState(null); // "deactivate" | "offboard" | "reset_pw"
  const [actionBusy, setActionBusy] = useState(false);
  const [actionMsg,  setActionMsg]  = useState(null);

  const token = localStorage.getItem("token");

  useEffect(() => {
    if (!user?.id) return;
    setLoading(true);
    fetch(`${API_BASE}/api/admin/users/${user.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => {
        setData(d);
        const normalised = { ...d };
        // Set draft.roles as comma-string for RoleMultiSelect
        if (Array.isArray(d.roles) && d.roles.length > 0) {
          normalised.roles = d.roles.join(",");
        } else if (d.role) {
          normalised.roles = d.role;
        }
        setDraft(normalised);
      })
      .catch(() => setData(user))
      .finally(() => setLoading(false));
  }, [user?.id]);

  if (!user) return null;

  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true); setError(null);
    try {
      // Build query params — backend PATCH expects these as Query params.
      // NOTE: activation status (is_active) is intentionally NOT sent here.
      // A profile/account edit must never change whether a user can log in —
      // that is owned solely by the Deactivate/Reactivate actions. Sending it
      // from here previously deactivated users whose derived status was
      // "pending" (active but not-yet-logged-in).
      const params = new URLSearchParams();
      if (draft.email)     params.append("email",     draft.email);
      if (draft.full_name) params.append("full_name",  draft.full_name);

      // roles — draft.roles is comma-separated string from RoleMultiSelect
      if (draft.roles) params.append("roles", draft.roles);

      const res = await fetch(
        `${API_BASE}/api/admin/users/${user.id}?${params.toString()}`,
        { method: "PATCH", headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updated = await res.json();

      // If editing the currently logged-in user, update localStorage
      // so FABs and RoleRoute reflect new roles immediately without re-login
      try {
        const me = JSON.parse(localStorage.getItem("user") || "null");
        if (me && me.id === user.id && updated?.data) {
          const d = updated.data;
          localStorage.setItem("user", JSON.stringify({
            ...me,
            role:  d.role,
            roles: Array.isArray(d.roles) ? d.roles : (d.roles || "").split(",").filter(Boolean),
          }));
          // Dispatch storage event so other components re-render
          window.dispatchEvent(new Event("storage"));
        }
      } catch {}

      setSaved(true);
      onSaved?.();
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const runAction = async (action) => {
    setActionBusy(true); setActionMsg(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${user.id}/${action}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setActionMsg(`✓ ${action.replace("_", " ")} successful`);
      onSaved?.();
    } catch (e) { setActionMsg(`⚠ ${e.message}`); }
    finally { setActionBusy(false); setConfirm(null); }
  };

  const name   = data?.name || data?.full_name || `${data?.first_name || ""} ${data?.last_name || ""}`.trim() || "User";
  const status = (data?.status || "active").toLowerCase();

  return (
    <div className="fixed inset-0 z-[10060]">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      <div className="absolute right-0 top-0 h-full w-full sm:w-[520px] bg-[#0d0d0d] border-l border-[#00ff90]/20 shadow-[-20px_0_60px_#00ff9011] text-white flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 shrink-0">
          <div>
            <p className="font-semibold text-white">{name}</p>
            <div className="flex items-center gap-2 mt-0.5">
              <p className="text-xs text-[#00ff90]/50">{data?.email}</p>
              <span className={`text-xs px-2 py-0.5 rounded-full border capitalize ${
                status === "active" ? "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/20" :
                status === "inactive" ? "text-red-400 bg-red-500/10 border-red-500/20" :
                "text-white/20 bg-white/5 border-white/10"
              }`}>{status}</span>
            </div>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/40 hover:text-white transition-colors">
            <X size={14} />
          </button>
        </div>

        {/* Quick actions bar */}
        <div className="px-5 py-3 border-b border-white/5 flex gap-2 flex-wrap shrink-0">
          {[
            { key: "deactivate",  label: status === "active" ? "Deactivate" : "Reactivate", style: "border-red-500/25 text-red-400/70 hover:text-red-400"       },
            { key: "reset_pw",    label: "Reset Password",  style: "border-yellow-500/25 text-yellow-400/70 hover:text-yellow-400" },
            { key: "offboard",    label: "Offboard",        style: "border-orange-500/25 text-orange-400/70 hover:text-orange-400" },
          ].map(({ key, label, style }) => (
            <button key={key} onClick={() => setConfirm(key)}
              className={`text-xs px-3 py-1.5 rounded-lg border bg-white/3 hover:bg-white/8 transition-colors ${style}`}>
              {label}
            </button>
          ))}
          {actionMsg && <span className={`text-xs self-center ${actionMsg.startsWith("⚠") ? "text-red-400" : "text-[#00ff90]"}`}>{actionMsg}</span>}
        </div>

        {/* Fields */}
        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {loading ? (
            <div className="space-y-3">{[1,2,3,4,5].map(i => <div key={i} className="h-10 rounded-lg bg-white/5 animate-pulse" />)}</div>
          ) : (
            Object.entries(SECTIONS).map(([section, fields]) => (
              <div key={section}>
                <p className="text-xs text-[#00ff90]/40 uppercase tracking-widest font-medium mb-3 pb-1 border-b border-white/5">{section}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {fields.map(k => (
                    <div key={k} className={k === "role" ? "sm:col-span-2" : ""}>
                      <Field fieldKey={k} value={draft[k]} onChange={v => set(k, v)} draft={draft} setDraft={setDraft} />
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}

          {/* Staffology Phase 2 — Payroll Details. Loads + saves via its
              own Phase-2 endpoints (independent of the account-field save
              above); UK-gated by the backend. */}
          {!loading && user?.id && <PayrollDetailsSection userId={user.id} />}
        </div>

        {/* Save footer */}
        <div className="px-5 py-4 border-t border-white/5 flex items-center justify-between shrink-0">
          {error  && <p className="text-red-400 text-xs">⚠ {error}</p>}
          {saved  && <p className="text-[#00ff90] text-xs">✓ Saved</p>}
          {!error && !saved && <span />}
          <button onClick={save} disabled={saving}
            className="text-xs px-5 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium">
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      {/* Confirm modal */}
      {confirm && (
        <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-10">
          <div className="w-full max-w-sm mx-4 rounded-xl border border-orange-500/30 bg-[#0d0d0d] p-6 space-y-4">
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} className="text-orange-400" />
              <h3 className="text-sm font-semibold text-white capitalize">{confirm.replace("_", " ")} User</h3>
            </div>
            <p className="text-white/50 text-sm">
              {confirm === "offboard" && "This will trigger the full offboarding process — access revocation, final payslip and document archival."}
              {confirm === "deactivate" && "This will disable the user's access immediately."}
              {confirm === "reset_pw" && "A password reset email will be sent to the user."}
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirm(null)} className="text-xs px-4 py-2 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">Cancel</button>
              <button onClick={() => runAction(confirm)} disabled={actionBusy}
                className="text-xs px-5 py-2 rounded-lg bg-orange-500/15 text-orange-400 border border-orange-500/30 hover:bg-orange-500/25 disabled:opacity-40 transition-colors font-medium">
                {actionBusy ? "Processing…" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}